import {
  DocumentType,
  SpecificDocumentType,
  PDFAnalysisResult,
  ExtractedPedigreeData,
  ExtractedMicrochipData,
  ExtractedGeneticTestData,
  ExtractedUnifiedData,
} from '@/types/pdfAnalysis';
import { pdfPromptBuilder } from './pdfPromptBuilder';
import { getGeminiModel, type GenerativeModel } from './geminiClient';
import { PDF_ANALYSIS_CONFIG, AI_ANALYSIS_DAILY_LIMIT } from './config';
import { aiAnalysisHistoryService } from '@/services/firebase/aiAnalysisHistoryService';

/**
 * PDF解析サービス
 * Gemini Vision APIを使用してPDFや画像からデータを抽出
 */
export class PDFAnalysisService {
  private model: GenerativeModel | null = null;

  /**
   * Gemini API（Firebase AI Logic経由）を初期化
   */
  private initialize(): void {
    if (this.model) {
      return;
    }

    this.model = getGeminiModel({
      model: PDF_ANALYSIS_CONFIG.model,
      maxOutputTokens: PDF_ANALYSIS_CONFIG.maxOutputTokens,
      temperature: PDF_ANALYSIS_CONFIG.temperature,
    });
  }

  /**
   * PDFまたは画像ファイルを解析
   * @param file 解析するファイル
   * @param documentType ドキュメントタイプ
   * @param userId ユーザーID
   * @returns 解析結果
   */
  async analyzePDF(
    file: File,
    documentType: DocumentType,
    userId: string
  ): Promise<PDFAnalysisResult> {
    // エラー時に使用するドキュメントタイプ（autoの場合はpedigreeを使用）
    const errorDocumentType: SpecificDocumentType = documentType === 'auto' ? 'pedigree' : documentType;

    try {
      // ファイルサイズチェック
      const maxSizeBytes = PDF_ANALYSIS_CONFIG.maxFileSizeMB * 1024 * 1024;
      if (file.size > maxSizeBytes) {
        return this.createErrorResult(
          errorDocumentType,
          `ファイルサイズが大きすぎます。最大${PDF_ANALYSIS_CONFIG.maxFileSizeMB}MBまでです。`
        );
      }

      // ファイルタイプチェック
      if (!PDF_ANALYSIS_CONFIG.supportedTypes.includes(file.type as typeof PDF_ANALYSIS_CONFIG.supportedTypes[number])) {
        return this.createErrorResult(
          errorDocumentType,
          `サポートされていないファイル形式です。対応形式: PDF, JPEG, PNG`
        );
      }

      // 日次上限チェック
      await this.checkDailyLimit(userId);

      // APIを初期化
      this.initialize();

      // ファイルをBase64に変換
      const base64Data = await this.fileToBase64(file);
      const mimeType = file.type;

      // プロンプトを生成
      const prompt = pdfPromptBuilder.buildPrompt(documentType);

      // Gemini Vision APIを呼び出し
      const rawResponse = await this.callGeminiVisionAPI(base64Data, mimeType, prompt);

      // レスポンスをパース
      // autoタイプの場合は統合レスポンスをパース
      const result = documentType === 'auto'
        ? this.parseUnifiedResponse(rawResponse)
        : this.parseResponse(rawResponse, documentType);

      return result;
    } catch (error) {
      console.error('PDF解析エラー:', error);
      const errorMessage = error instanceof Error ? error.message : 'PDF解析中にエラーが発生しました';
      return this.createErrorResult(errorDocumentType, errorMessage);
    }
  }

  /**
   * 日次上限チェック
   */
  private async checkDailyLimit(userId: string): Promise<void> {
    const todayCount = await aiAnalysisHistoryService.getTodayCount(userId);
    if (todayCount >= AI_ANALYSIS_DAILY_LIMIT) {
      throw new Error(
        `本日のAI分析上限（${AI_ANALYSIS_DAILY_LIMIT}回）に達しました。明日以降にお試しください。`
      );
    }
  }

  /**
   * ファイルをBase64に変換
   */
  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // data:image/jpeg;base64, の部分を除去
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Gemini Vision APIを呼び出し
   */
  private async callGeminiVisionAPI(
    base64Data: string,
    mimeType: string,
    prompt: string
  ): Promise<string> {
    if (!this.model) {
      throw new Error('Gemini APIが初期化されていません');
    }

    const result = await this.model.generateContent([
      {
        inlineData: {
          mimeType,
          data: base64Data,
        },
      },
      { text: prompt },
    ]);

    const response = result.response;
    const text = response.text();

    return text;
  }

  /**
   * レスポンスをパースして型付きの結果に変換
   * @param responseText AIからのレスポンステキスト
   * @param documentType 具体的なドキュメントタイプ（auto以外）
   */
  private parseResponse(
    responseText: string,
    documentType: SpecificDocumentType
  ): PDFAnalysisResult {
    try {
      // JSONブロックを抽出
      const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
      const jsonStr = jsonMatch ? jsonMatch[1] : responseText;

      // JSONをパース
      const parsed = JSON.parse(jsonStr.trim());

      const confidence = parsed.confidence ?? 70;
      const warnings = parsed.warnings ?? [];

      switch (documentType) {
        case 'pedigree':
          return {
            success: true,
            documentType,
            extractedData: this.extractPedigreeData(parsed),
            confidence,
            warnings,
          };
        case 'microchip':
          return {
            success: true,
            documentType,
            extractedData: this.extractMicrochipData(parsed),
            confidence,
            warnings,
          };
        case 'geneticTest':
          return {
            success: true,
            documentType,
            extractedData: this.extractGeneticTestData(parsed),
            confidence,
            warnings,
          };
        default: {
          // exhaustive check
          const _exhaustiveCheck: never = documentType;
          throw new Error(`不明なドキュメントタイプ: ${_exhaustiveCheck}`);
        }
      }
    } catch (error) {
      console.error('レスポンスのパースエラー:', error);
      console.error('Response text:', responseText);
      return this.createErrorResult(
        documentType,
        'AIからの応答を解析できませんでした。画像の品質を確認してください。'
      );
    }
  }

  /**
   * 血統書データを抽出
   */
  private extractPedigreeData(parsed: Record<string, unknown>): ExtractedPedigreeData {
    const ancestors = parsed.ancestors as Record<string, unknown> | undefined;

    return {
      officialName: this.normalizeString(parsed.officialName),
      breed: this.normalizeString(parsed.breed),
      jkcRegistrationNumber: this.normalizeString(parsed.jkcRegistrationNumber),
      dnaRegistrationNumber: this.normalizeString(parsed.dnaRegistrationNumber),
      gender: this.normalizeGender(parsed.gender),
      birthDate: this.normalizeDate(parsed.birthDate),
      color: this.normalizeString(parsed.color),
      breederName: this.normalizeString(parsed.breederName),
      ownerName: this.normalizeString(parsed.ownerName),
      ancestors: ancestors ? {
        father: this.normalizeAncestor(ancestors.father),
        mother: this.normalizeAncestor(ancestors.mother),
        paternalGrandfather: this.normalizeAncestor(ancestors.paternalGrandfather),
        paternalGrandmother: this.normalizeAncestor(ancestors.paternalGrandmother),
        maternalGrandfather: this.normalizeAncestor(ancestors.maternalGrandfather),
        maternalGrandmother: this.normalizeAncestor(ancestors.maternalGrandmother),
      } : undefined,
    };
  }

  /**
   * マイクロチップデータを抽出
   */
  private extractMicrochipData(parsed: Record<string, unknown>): ExtractedMicrochipData {
    return {
      microchipNumber: this.normalizeString(parsed.microchipNumber) ?? '',
      registrationDate: this.normalizeDate(parsed.registrationDate),
      pin: this.normalizeString(parsed.pin),
    };
  }

  /**
   * 遺伝子検査データを抽出
   */
  private extractGeneticTestData(parsed: Record<string, unknown>): ExtractedGeneticTestData {
    const testResults = (parsed.testResults as Array<Record<string, unknown>>) ?? [];

    return {
      animalName: this.normalizeString(parsed.animalName),
      testResults: testResults.map((item) => ({
        testName: this.normalizeString(item.testName) ?? '',
        result: this.normalizeGeneticResult(item.result),
      })).filter(item => item.testName),
      testDate: this.normalizeDate(parsed.testDate),
      laboratory: this.normalizeString(parsed.laboratory),
    };
  }

  /**
   * 文字列を正規化
   */
  private normalizeString(value: unknown): string | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    return String(value).trim();
  }

  /**
   * 性別を正規化
   */
  private normalizeGender(value: unknown): 'male' | 'female' | undefined {
    if (value === 'male' || value === 'female') {
      return value;
    }
    return undefined;
  }

  /**
   * 日付を正規化（YYYY-MM-DD形式）
   */
  private normalizeDate(value: unknown): string | undefined {
    if (!value || value === null) {
      return undefined;
    }
    const dateStr = String(value).trim();
    // YYYY-MM-DD形式かチェック
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    return undefined;
  }

  /**
   * 先祖情報を正規化
   */
  private normalizeAncestor(
    value: unknown
  ): { name?: string; registrationNumber?: string } | undefined {
    if (!value || typeof value !== 'object') {
      return undefined;
    }
    const obj = value as Record<string, unknown>;
    const name = this.normalizeString(obj.name);
    const registrationNumber = this.normalizeString(obj.registrationNumber);
    if (!name && !registrationNumber) {
      return undefined;
    }
    return { name, registrationNumber };
  }

  /**
   * 遺伝子検査結果を正規化
   */
  private normalizeGeneticResult(
    value: unknown
  ): 'clear' | 'carrier' | 'affected' | 'unknown' {
    const strValue = String(value).toLowerCase().trim();
    if (strValue === 'clear' || strValue === 'クリア' || strValue === 'n/n' || strValue === '-/-') {
      return 'clear';
    }
    if (strValue === 'carrier' || strValue === 'キャリア' || strValue === 'n/a' || strValue === '+/-') {
      return 'carrier';
    }
    if (strValue === 'affected' || strValue === 'アフェクテッド' || strValue === 'a/a' || strValue === '+/+') {
      return 'affected';
    }
    return 'unknown';
  }

  /**
   * 統合レスポンスをパースして型付きの結果に変換
   * autoタイプの場合に使用される
   */
  private parseUnifiedResponse(responseText: string): PDFAnalysisResult {
    try {
      // デバッグログ: AIレスポンスの全文を出力
      console.log('[PDF解析] AIレスポンス (raw):', responseText);

      // JSONブロックを抽出
      const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
      const jsonStr = jsonMatch ? jsonMatch[1] : responseText;

      // デバッグログ: 抽出されたJSON文字列
      console.log('[PDF解析] 抽出されたJSON:', jsonStr);

      // JSONをパース
      const parsed = JSON.parse(jsonStr.trim());

      // デバッグログ: パースされたオブジェクト
      console.log('[PDF解析] パース結果:', parsed);

      // 配列形式のレスポンスに対応（複数ドキュメントをマージ）
      if (Array.isArray(parsed)) {
        console.log('[PDF解析] 配列形式のレスポンスを検出。件数:', parsed.length);
        if (parsed.length === 0) {
          return this.createErrorResult('pedigree', 'ドキュメントが検出されませんでした。');
        }
        // 複数ドキュメントをマージ
        return this.mergeMultipleDocuments(parsed);
      }

      // 単一オブジェクトの場合は既存ロジック
      // ドキュメントタイプの検証（型安全）
      const validTypes: SpecificDocumentType[] = ['pedigree', 'microchip', 'geneticTest'];
      if (!parsed.documentType) {
        console.warn('[PDF解析] documentTypeが未指定です');
        return this.createErrorResult(
          'pedigree',
          'ドキュメントの種類を判定できませんでした。血統書、マイクロチップ証明書、または遺伝子検査結果の画像をアップロードしてください。'
        );
      }
      if (!validTypes.includes(parsed.documentType)) {
        console.warn('[PDF解析] 無効なdocumentType:', parsed.documentType);
        return this.createErrorResult(
          'pedigree',
          `不明なドキュメントタイプ: ${parsed.documentType}`
        );
      }

      const detectedType = parsed.documentType as SpecificDocumentType;
      // confidence: 数値でない場合はデフォルト値
      const confidence = typeof parsed.confidence === 'number'
        ? Math.min(100, Math.max(0, parsed.confidence))
        : 70;
      // warnings: 配列でない場合は空配列
      const warnings = Array.isArray(parsed.warnings) ? parsed.warnings : [];
      // data: オブジェクトでない場合は空オブジェクト
      const data = (typeof parsed.data === 'object' && parsed.data !== null) ? parsed.data : {};

      // 検出されたタイプに基づいてデータを抽出
      switch (detectedType) {
        case 'pedigree':
          return {
            success: true,
            documentType: detectedType,
            extractedData: this.extractPedigreeData(data),
            confidence,
            warnings,
          };
        case 'microchip':
          return {
            success: true,
            documentType: detectedType,
            extractedData: this.extractMicrochipData(data),
            confidence,
            warnings,
          };
        case 'geneticTest':
          return {
            success: true,
            documentType: detectedType,
            extractedData: this.extractGeneticTestData(data),
            confidence,
            warnings,
          };
        default: {
          // exhaustive check: 全ての有効なケースは上で処理されている
          const _exhaustiveCheck: never = detectedType;
          return this.createErrorResult(
            'pedigree',
            `サポートされていないドキュメントタイプ: ${_exhaustiveCheck}`
          );
        }
      }
    } catch (error) {
      console.error('統合レスポンスのパースエラー:', error);
      console.error('Response text:', responseText);
      return this.createErrorResult(
        'pedigree',
        'AIからの応答を解析できませんでした。画像の品質を確認してください。'
      );
    }
  }

  /**
   * 複数ドキュメントをマージして統合結果を作成
   */
  private mergeMultipleDocuments(documents: Array<Record<string, unknown>>): PDFAnalysisResult {
    const unifiedData: ExtractedUnifiedData = {};
    let totalConfidence = 0;
    const allWarnings: string[] = [];

    for (const doc of documents) {
      const docType = doc.documentType as string;
      const data = (doc.data as Record<string, unknown>) || {};
      const confidence = typeof doc.confidence === 'number' ? doc.confidence : 70;
      totalConfidence += confidence;

      if (Array.isArray(doc.warnings)) {
        allWarnings.push(...(doc.warnings as string[]));
      }

      switch (docType) {
        case 'pedigree': {
          // 血統書データをベースに追加
          const pedigreeData = this.extractPedigreeData(data);
          Object.assign(unifiedData, pedigreeData);
          break;
        }
        case 'microchip': {
          // マイクロチップデータを追加
          const microchipData = this.extractMicrochipData(data);
          unifiedData.microchipNumber = microchipData.microchipNumber;
          unifiedData.microchipRegistrationDate = microchipData.registrationDate;
          unifiedData.microchipPin = microchipData.pin;
          break;
        }
        case 'geneticTest': {
          // 遺伝子検査データを追加
          const geneticData = this.extractGeneticTestData(data);
          unifiedData.geneticTestResults = geneticData.testResults;
          unifiedData.geneticTestDate = geneticData.testDate;
          unifiedData.geneticTestLaboratory = geneticData.laboratory;
          unifiedData.animalName = geneticData.animalName;
          break;
        }
        default:
          console.warn('[PDF解析] 不明なドキュメントタイプをスキップ:', docType);
      }
    }

    const avgConfidence = Math.round(totalConfidence / documents.length);

    console.log('[PDF解析] マージ結果:', unifiedData);

    return {
      success: true,
      documentType: 'pedigree', // 統合データは血統書として扱う
      extractedData: unifiedData,
      confidence: avgConfidence,
      warnings: allWarnings.length > 0 ? allWarnings : undefined,
    };
  }

  /**
   * エラー結果を作成
   * @param documentType 具体的なドキュメントタイプ（auto以外）
   * @param errorMessage エラーメッセージ
   */
  private createErrorResult(
    documentType: SpecificDocumentType,
    errorMessage: string
  ): PDFAnalysisResult {
    const emptyData: ExtractedPedigreeData | ExtractedMicrochipData | ExtractedGeneticTestData =
      documentType === 'pedigree'
        ? {}
        : documentType === 'microchip'
          ? { microchipNumber: '' }
          : { testResults: [] };

    return {
      success: false,
      documentType,
      extractedData: emptyData,
      confidence: 0,
      error: errorMessage,
    };
  }
}

// シングルトンインスタンスをエクスポート
export const pdfAnalysisService = new PDFAnalysisService();
